import { useMediaQuery } from "react-responsive";

export default function useResponsive() {
  const isDesktop = useMediaQuery({ minWidth: 1025 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const isMobile = useMediaQuery({ maxWidth: 500 });
  // Devices without a fine hover pointer (phones/tablets). Used to surface
  // hover-only chart affordances (legends, folded series, tooltips) that a
  // touch user can never trigger.
  const isTouch = useMediaQuery({ query: "(hover: none)" });
  const isXLDesktop = useMediaQuery({ minWidth: 1280 });
  const is1XLDesktop = useMediaQuery({ minWidth: 1440 });
  const is2XLDesktop = useMediaQuery({ minWidth: 1536 });
  const is3XLDesktop = useMediaQuery({ minWidth: 1700 });
  const isFullHDPlus = useMediaQuery({ minWidth: 1920 });
  const isLargeDesktop = useMediaQuery({ minWidth: 1921 });
  const is2KDesktop = useMediaQuery({ minWidth: 2200 });
  const isTallScreen = useMediaQuery({ minHeight: 1000 });
  const isMobileLandScape = useMediaQuery({ maxWidth: 767 });
  const isTall2 = useMediaQuery({ maxHeight: 800 });
  const isTall2XLDesktop = useMediaQuery({ maxHeight: 900 });
  const isTall3XLDesktop = useMediaQuery({ minHeight: 1000, minWidth: 1920 });
  const isQHD = useMediaQuery({ minHeight: 1200, minWidth: 2260 });
  const isQHD2 = useMediaQuery({ minHeight: 1280, minWidth: 2560 });
  const is1080p = useMediaQuery({ minWidth: 1920, maxWidth: 2149, maxHeight: 1080 });
  const isNarrowScreen = useMediaQuery({ maxWidth: 1157 });

  // Enhanced fluid responsiveness functions
  const getFluidScale = (min: number, max: number, viewport: number) => {
    const clamped = Math.max(320, Math.min(viewport, 2560));
    const ratio = (clamped - 320) / (2560 - 320);
    return min + (max - min) * ratio;
  };

  const getFluidSpacing = (min: number, max: number) => {
    if (typeof window !== "undefined") {
      return getFluidScale(min, max, window.innerWidth);
    }
    return min;
  };

  const getFluidColumns = (minCols: number, maxCols: number) => {
    if (typeof window !== "undefined") {
      const width = window.innerWidth;
      if (width < 640) return 1;
      if (width < 768) return 2;
      if (width < 1024) return 3;
      if (width < 1280) return 4;
      return maxCols;
    }
    return minCols;
  };

  // Calendar-specific responsive functions
  const getCalendarLayout = () => {
    if (isMobile) return "mobile";
    if (isTablet) return "tablet";
    return "desktop";
  };

  const getCalendarGridSize = () => {
    if (isMobile) return "small";
    if (isTablet) return "medium";
    return "large";
  };

  const getCalendarSpacing = () => {
    if (isMobile) return "compact";
    if (isTablet) return "medium";
    return "comfortable";
  };

  return {
    isDesktop,
    isTablet,
    isMobile,
    isTouch,
    isXLDesktop,
    is1XLDesktop,
    is2XLDesktop,
    is3XLDesktop,
    isFullHDPlus,
    isLargeDesktop,
    is2KDesktop,
    is1080p,
    isQHD,
    isQHD2,
    isNarrowScreen,
    isMobileLandScape,
    isAnyMobile: isMobile || isMobileLandScape,
    isSmallDevice: isMobile || isTablet || isMobileLandScape,
    isTallScreen,
    isTall2,
    isTall2XLDesktop,
    isTall3XLDesktop,
    // Enhanced fluid responsiveness
    getFluidScale,
    getFluidSpacing,
    getFluidColumns,
    // Calendar responsive functions
    getCalendarLayout,
    getCalendarGridSize,
    getCalendarSpacing,
  };
}
